import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import DemoTemplate from './DemoTemplate';

// Token-based prospect demo. Fetches the demo data from /api/p/:token, renders
// with the shared DemoTemplate. Adds a `noindex` meta tag so search engines
// never list these private URLs.
function ProspectDemoPage() {
  const { token } = useParams();
  const [state, setState] = useState({ status: 'loading', demo: null, error: null });

  useEffect(() => {
    window.scrollTo(0, 0);

    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    return () => { document.head.removeChild(meta); };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading', demo: null, error: null });

    fetch(`/api/p/${encodeURIComponent(token)}`)
      .then(async (res) => {
        const body = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (res.status === 410) {
          setState({ status: 'expired', demo: null, error: body.error || 'This demo has expired.' });
        } else if (res.status === 404) {
          setState({ status: 'not_found', demo: null, error: 'Demo not found.' });
        } else if (!res.ok || !body.ok) {
          setState({ status: 'error', demo: null, error: body.error || `Request failed (${res.status})` });
        } else {
          setState({ status: 'ok', demo: body.demo, error: null });
        }
      })
      .catch((err) => {
        if (!cancelled) setState({ status: 'error', demo: null, error: err.message });
      });

    return () => { cancelled = true; };
  }, [token]);

  if (state.status === 'loading') {
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 mx-auto mb-4 rounded-full border-2 border-accent border-t-transparent animate-spin" />
          <p className="text-text-muted text-sm">Loading your demo…</p>
        </div>
      </div>
    );
  }

  if (state.status !== 'ok') {
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <h1 className="font-display font-bold text-3xl text-text mb-3">
            {state.status === 'expired' ? 'Demo expired' : 'Demo not found'}
          </h1>
          <p className="text-text-muted mb-8">
            {state.error || "We couldn't find that demo. The link may be incorrect or no longer active."}
          </p>
          <Link to="/" className="inline-flex items-center gap-2 bg-gradient-brand text-white px-6 py-3 rounded-full font-semibold hover:shadow-glow transition-shadow">
            Visit Sudco Solutions
          </Link>
        </div>
      </div>
    );
  }

  return <DemoTemplate demo={state.demo} />;
}

export default ProspectDemoPage;
